import type { BaseResponse, Event } from "@anggunpraya-templates/types";
import { EVENT_GUEST_ATTENDING_WITH_OBJECT } from "@anggunpraya-templates/base/app/constant";
import { useEventGuestStore } from "../stores/event-guest";
import { useTemplateStore } from "../stores/template";
import { usePackageCapabilities } from "./use-package-capabilities";

/**
 * Composable shared untuk derived state section RSVP.
 *
 * Tujuannya agar komponen UI tidak perlu tahu cara menghitung:
 * - deadline RSVP
 * - disabled state
 * - teks pendamping guest
 * - format tanggal yang siap tampil
 *
 * @returns Derived state yang siap dipakai komponen section RSVP
 *
 * @example
 * ```typescript
 * const {
 *   guestName,
 *   attendingWithText,
 *   isRsvpDisabled,
 *   formattedRsvpDeadline,
 * } = useRsvpSection();
 * ```
 */
export function useRsvpSection() {
  const { $dayjs } = useNuxtApp();
  const { flavor } = useRuntimeConfig().public;

  const { eventGuest } = storeToRefs(useEventGuestStore());
  const { supportsRsvp } = usePackageCapabilities();

  const templateStore = useTemplateStore();
  const { template } = storeToRefs(templateStore);
  const { setIsRsvpDisabled } = templateStore;

  const { data: eventDetail } = useNuxtData<BaseResponse<Event>>("event-detail");

  /** Deadline RSVP final yang dipakai UI, termasuk fallback preview. */
  const rsvpDeadline = computed(() => {
    if (!supportsRsvp.value || !eventDetail.value?.data?.rsvp_enabled) {
      return null;
    }

    if (["event", "portfolio"].includes(flavor)) {
      return eventDetail.value.data.rsvp_deadline;
    }

    const eventDate = $dayjs(template.value.general.date);

    return eventDate.subtract(2, "day").endOf("day").toISOString();
  });

  /** Shared policy untuk menutup RSVP saat deadline lewat. */
  const isRsvpDisabled = computed(() => {
    if (!supportsRsvp.value || !eventDetail.value?.data?.rsvp_enabled) {
      return true;
    }

    if (!rsvpDeadline.value || flavor === "preview") {
      return false;
    }

    return !$dayjs().isBefore($dayjs(rsvpDeadline.value));
  });

  /** Teks `attending_with` yang sudah siap ditampilkan. */
  const attendingWithText = computed(() => {
    if (!eventGuest.value?.attending_with || eventGuest.value.attending_with === "alone") {
      return "";
    }

    return `${EVENT_GUEST_ATTENDING_WITH_OBJECT[eventGuest.value.attending_with]}`.trim();
  });

  const guestName = computed(() => eventGuest.value?.name || "");

  /** Format datetime yang dipakai untuk atribut `datetime` di markup. */
  const formattedEventDateTime = computed(() => {
    return $dayjs(template.value.general.date).format("YYYY-MM-DD HH:mm");
  });

  /** Format human-readable untuk deadline RSVP. */
  const formattedRsvpDeadline = computed(() => {
    if (!rsvpDeadline.value) {
      return "";
    }

    return $dayjs(rsvpDeadline.value).format("dddd, D MMMM YYYY");
  });

  /** Sinkronkan hasil perhitungan deadline ke template store agar flow lain bisa memakainya. */
  watch(
    () => isRsvpDisabled.value,
    (value) => {
      setIsRsvpDisabled(value);
    },
    {
      immediate: true,
    },
  );

  return {
    eventDetail,
    guestName,
    attendingWithText,
    isRsvpDisabled,
    supportsRsvp,
    formattedEventDateTime,
    formattedRsvpDeadline,
    rsvpDeadline,
  };
}
